import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useCreateUser, useMe, useProjects, useResetUser, useUpdateUser, useUsers } from "@/api/hooks";
import type { Locale } from "@/api/types/Locale";
import type { Role } from "@/api/types/Role";
import type { UserSummary } from "@/api/types/UserSummary";
import type { UserWithPassword } from "@/api/types/UserWithPassword";
import { ConfirmDialog, Dialog } from "@/components/Dialog";
import { ProjectLogo } from "@/components/ProjectLogo";
import { useToast } from "@/components/Toast";
import { Avatar, BotTag, Button, Checkbox, Chip, EmptyState, Field, Input, LoadError, PageHeader, PageSpinner, RoleChip, Select } from "@/components/ui";
import { LOCALES } from "@/i18n";
import { fullDate, relativeTime } from "@/lib/format";
import { useCopy } from "@/lib/useCopy";
import { ROLES, RoleOverrideOptions } from "@/pages/project/ProjectMembers";
import { DOCS_URL, TokenManager } from "@/pages/CliTokensSection";

const USERNAME_RE = /^[a-z0-9][a-z0-9._-]{1,31}$/;

export function UsersPage() {
  const { t } = useTranslation();
  const users = useUsers();
  const [role, setRole] = useState<Role | "">("");
  const [status, setStatus] = useState<"" | "active" | "disabled">("");
  const [kind, setKind] = useState<"" | "people" | "bots">("");
  /** Which kind of account the create dialog makes; `null` = closed. */
  const [creating, setCreating] = useState<"user" | "bot" | null>(null);
  const [botTokens, setBotTokens] = useState<UserSummary | null>(null);
  const [editing, setEditing] = useState<UserSummary | null>(null);
  const [resetting, setResetting] = useState<UserSummary | null>(null);
  const [shown, setShown] = useState<UserWithPassword | null>(null);

  const filtered = useMemo(
    () =>
      (users.data ?? []).filter(
        (u) =>
          (!role || u.role === role) &&
          (!status || (status === "disabled" ? u.disabled : !u.disabled)) &&
          (!kind || (kind === "bots") === u.is_bot),
      ),
    [users.data, role, status, kind],
  );

  return (
    <div className="flex flex-col gap-5 p-6">
      <PageHeader
        title={t("users.title")}
        actions={
          <>
            <Button icon="bot" onClick={() => setCreating("bot")}>
              {t("users.newBot")}
            </Button>
            <Button variant="primary" icon="plus" onClick={() => setCreating("user")}>
              {t("users.newUser")}
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap gap-3">
        <label className="flex items-center gap-2 text-sm text-muted">
          {t("users.filterRole")}
          <Select value={role} onChange={(e) => setRole(e.target.value as Role | "")} className="w-auto">
            <option value="">{t("users.allRoles")}</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`roles.${r}`)}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex items-center gap-2 text-sm text-muted">
          {t("users.filterStatus")}
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as "" | "active" | "disabled")}
            className="w-auto"
          >
            <option value="">{t("users.allStatuses")}</option>
            <option value="active">{t("users.active")}</option>
            <option value="disabled">{t("users.disabled")}</option>
          </Select>
        </label>
        <label className="flex items-center gap-2 text-sm text-muted">
          {t("users.filterKind")}
          <Select value={kind} onChange={(e) => setKind(e.target.value as "" | "people" | "bots")} className="w-auto">
            <option value="">{t("users.allKinds")}</option>
            <option value="people">{t("users.people")}</option>
            <option value="bots">{t("users.bots")}</option>
          </Select>
        </label>
      </div>

      {users.isPending ? (
        <PageSpinner />
      ) : users.isError ? (
        <LoadError onRetry={() => void users.refetch()} />
      ) : (
        <div className="card relative overflow-x-auto">
          <table className="table-base min-w-[860px]">
            <thead>
              <tr>
                <th>{t("users.name")}</th>
                <th>{t("users.role")}</th>
                <th>{t("users.access")}</th>
                <th>{t("users.status")}</th>
                <th>{t("users.projects")}</th>
                <th>{t("users.lastActive")}</th>
                <th className="w-px">
                  <span className="sr-only">{t("common.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  onEdit={() => setEditing(u)}
                  onReset={() => setResetting(u)}
                  onTokens={() => setBotTokens(u)}
                />
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <EmptyState icon="users" title={t("users.empty")} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <CreateUserDialog
        kind={creating}
        onClose={() => setCreating(null)}
        onCreated={(res) => (res.user.is_bot ? setBotTokens(res.user) : setShown(res))}
      />
      <BotTokensDialog bot={botTokens} onClose={() => setBotTokens(null)} />
      <EditUserDialog user={editing} onClose={() => setEditing(null)} />
      <ResetDialog user={resetting} onClose={() => setResetting(null)} onReset={setShown} />
      <PasswordDialog result={shown} onClose={() => setShown(null)} />
    </div>
  );
}

function UserRow({
  user,
  onEdit,
  onReset,
  onTokens,
}: {
  user: UserSummary;
  onEdit: () => void;
  onReset: () => void;
  onTokens: () => void;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const locked = user.is_owner && !me.is_owner;
  const isMe = user.id === me.id;

  return (
    <tr>
      <td>
        <div className="flex items-center gap-2.5">
          <Avatar name={user.display_name} bot={user.is_bot} size={28} />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 font-medium">
              {user.display_name}
              {user.is_bot && <BotTag />}
              {isMe && <span className="font-normal text-muted">({t("users.you")})</span>}
              {user.is_owner && <Chip tone="accent">{t("users.owner")}</Chip>}
            </div>
            <div className="font-mono text-xs text-muted">{user.username}</div>
          </div>
        </div>
      </td>
      <td>
        <RoleChip role={user.role} />
      </td>
      <td>
        {user.is_bot ? (
          <Chip tone={user.token_count ? "neutral" : "outline"} icon="key">
            {t("users.tokenCount", { count: Number(user.token_count) })}
          </Chip>
        ) : user.totp_enabled ? (
          <Chip tone="pass" icon="check">
            {t("users.twoFactorOn")}
          </Chip>
        ) : (
          <Chip tone="outline">{t("users.twoFactorOff")}</Chip>
        )}
      </td>
      <td>
        {user.disabled ? <Chip tone="skip">{t("users.disabled")}</Chip> : <Chip tone="pass">{t("users.active")}</Chip>}
      </td>
      <td className="font-mono">{user.project_count}</td>
      <td className="whitespace-nowrap text-muted">
        <LastActive at={user.is_bot ? user.last_token_used_at : user.last_login_at} />
      </td>
      <td>
        {locked ? (
          <span className="text-xs text-muted" title={t("users.ownerLocked")}>
            —
          </span>
        ) : (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit}>
              {t("common.edit")}
            </Button>
            {user.is_bot ? (
              // Mixla's own account for GitLab moves: the server refuses it tokens.
              user.username !== "gitlab-bot" && (
                <Button variant="ghost" size="sm" icon="key" onClick={onTokens}>
                  {t("users.tokens")}
                </Button>
              )
            ) : (
              !isMe && (
                <Button variant="ghost" size="sm" icon="key" onClick={onReset}>
                  {t("users.reset")}
                </Button>
              )
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function LastActive({ at }: { at: string | null }) {
  const { t } = useTranslation();
  if (!at) return <>{t("common.never")}</>;
  return (
    <time dateTime={at} title={fullDate(at)}>
      {relativeTime(at)}
    </time>
  );
}

function CreateUserDialog({
  kind,
  onClose,
  onCreated,
}: {
  kind: "user" | "bot" | null;
  onClose: () => void;
  onCreated: (r: UserWithPassword) => void;
}) {
  const { t } = useTranslation();
  const bot = kind === "bot";
  const toast = useToast();
  const create = useCreateUser();
  const projects = useProjects();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Role>("developer");
  const [locale, setLocale] = useState<Locale>("en");
  /** project id → role override ("" = same as platform role) */
  const [memberships, setMemberships] = useState<Record<string, string>>({});
  const usernameValid = USERNAME_RE.test(username);

  const close = () => {
    setUsername("");
    setDisplayName("");
    setRole("developer");
    setLocale("en");
    setMemberships({});
    onClose();
  };

  const toggle = (id: string, on: boolean) =>
    setMemberships((m) => {
      const next = { ...m };
      if (on) next[id] = "";
      else delete next[id];
      return next;
    });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        username,
        display_name: displayName.trim(),
        role,
        locale,
        bot,
        projects: Object.entries(memberships).map(([project_id, o]) => ({
          project_id,
          role_override: role === "manager" ? null : ((o || null) as Role | null),
        })),
      },
      {
        onSuccess: (res) => {
          toast.success(bot ? t("users.botCreated") : t("users.created"));
          close();
          onCreated(res);
        },
        onError: (err) => toast.error(err),
      },
    );
  };

  return (
    <Dialog
      open={!!kind}
      onClose={close}
      title={bot ? t("users.createBotTitle") : t("users.createTitle")}
      width={600}
      footer={
        <>
          <Button onClick={close}>{t("common.cancel")}</Button>
          <Button
            type="submit"
            form="new-user"
            variant="primary"
            loading={create.isPending}
            disabled={!usernameValid || !displayName.trim()}
          >
            {bot ? t("users.createBotButton") : t("users.createButton")}
          </Button>
        </>
      }
    >
      <form id="new-user" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {bot && (
          <p className="m-0 text-sm text-text-2">
            {t("users.botIntro")}{" "}
            <a href={DOCS_URL} target="_blank" rel="noreferrer">
              {t("tokens.apiDocs")}
            </a>
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("users.username")} hint={t("users.usernameHint")} error={username && !usernameValid ? t("users.usernameHint") : undefined}>
            {(p) => (
              <Input
                {...p}
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().trim())}
                className="font-mono"
                autoCapitalize="none"
                spellCheck={false}
                autoComplete="off"
                autoFocus
                required
              />
            )}
          </Field>
          <Field label={t("users.displayName")}>
            {(p) => (
              <Input {...p} value={displayName} onChange={(e) => setDisplayName(e.target.value)} required maxLength={80} />
            )}
          </Field>
          <Field label={t("users.role")}>
            {(p) => (
              <Select {...p} value={role} onChange={(e) => setRole(e.target.value as Role)}>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {t(`roles.${r}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {!bot && (
            <Field label={t("users.language")}>
              {(p) => (
                <Select {...p} value={locale} onChange={(e) => setLocale(e.target.value as Locale)}>
                  {LOCALES.map((l) => (
                    <option key={l} value={l}>
                      {t(`locales.${l}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
        </div>

        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="mb-1 text-sm font-medium text-text-2">{t("users.projectsLabel")}</legend>
          {projects.data?.length === 0 ? (
            <p className="m-0 text-xs text-muted">{t("users.noProjectsYet")}</p>
          ) : (
            <>
              <p className="m-0 text-xs text-muted">
                {role === "manager" ? t("members.managerAlways") : t("users.projectsHint")}
              </p>
              <ul className="m-0 flex list-none flex-col divide-y divide-border rounded-[var(--radius-ctl)] border border-border p-0">
                {projects.data?.map((p) => {
                  const on = p.id in memberships;
                  return (
                    <li key={p.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
                      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) => toggle(p.id, e.target.checked)}
                          className="size-4 accent-[var(--accent)]"
                        />
                        <ProjectLogo project={p} size={22} />
                        <span className="truncate">{p.name}</span>
                        <span className="font-mono text-xs text-muted">{p.key}</span>
                      </label>
                      {on && role !== "manager" && (
                        <Select
                          aria-label={`${t("users.roleInProject")}: ${p.name}`}
                          value={memberships[p.id]}
                          onChange={(e) => setMemberships((m) => ({ ...m, [p.id]: e.target.value }))}
                          className="w-auto min-w-[200px]"
                        >
                          <RoleOverrideOptions />
                        </Select>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </fieldset>
      </form>
    </Dialog>
  );
}

function EditUserDialog({ user, onClose }: { user: UserSummary | null; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open={!!user} onClose={onClose} title={t("users.editTitle", { name: user?.display_name ?? "" })} width={480}>
      {user && <EditUserForm key={user.id} user={user} onDone={onClose} />}
    </Dialog>
  );
}

function EditUserForm({ user, onDone }: { user: UserSummary; onDone: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const me = useMe();
  const update = useUpdateUser();
  const [displayName, setDisplayName] = useState(user.display_name);
  const [role, setRole] = useState<Role>(user.role);
  const [disabled, setDisabled] = useState(user.disabled);
  // Owners and you yourself can't be demoted or disabled from here.
  const protectedAccount = user.is_owner || user.id === me.id;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    update.mutate(
      {
        id: user.id,
        body: {
          display_name: displayName.trim() !== user.display_name ? displayName.trim() : null,
          role: role !== user.role ? role : null,
          disabled: disabled !== user.disabled ? disabled : null,
        },
      },
      {
        onSuccess: () => {
          toast.success(t("users.saved"));
          onDone();
        },
        onError: (err) => toast.error(err),
      },
    );
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <Field label={t("users.displayName")}>
        {(p) => <Input {...p} value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={80} />}
      </Field>
      <Field label={t("users.role")}>
        {(p) => (
          <Select {...p} value={role} onChange={(e) => setRole(e.target.value as Role)} disabled={protectedAccount}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`roles.${r}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {!protectedAccount && (
        <div className="flex flex-col gap-1">
          <Checkbox label={t("users.accountDisabled")} checked={disabled} onChange={(e) => setDisabled(e.target.checked)} />
          <span className="pl-6 text-xs text-muted">{t("users.accountDisabledHint")}</span>
        </div>
      )}
      <div className="flex justify-end gap-2 border-t border-border pt-4">
        <Button onClick={onDone}>{t("common.cancel")}</Button>
        <Button type="submit" variant="primary" loading={update.isPending} disabled={!displayName.trim()}>
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
}

function ResetDialog({
  user,
  onClose,
  onReset,
}: {
  user: UserSummary | null;
  onClose: () => void;
  onReset: (r: UserWithPassword) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const reset = useResetUser();
  return (
    <ConfirmDialog
      open={!!user}
      onClose={onClose}
      danger
      loading={reset.isPending}
      title={t("users.resetTitle", { name: user?.display_name ?? "" })}
      body={t("users.resetBody")}
      confirmLabel={t("users.resetConfirm")}
      onConfirm={() =>
        user &&
        reset.mutate(user.id, {
          onSuccess: (res) => {
            toast.success(t("users.resetDone"));
            onClose();
            onReset(res);
          },
          onError: (e) => toast.error(e),
        })
      }
    />
  );
}

/** Shows a generated password exactly once. Not dismissable by Esc/backdrop, so it isn't lost by accident. */
function PasswordDialog({ result, onClose }: { result: UserWithPassword | null; onClose: () => void }) {
  const { t } = useTranslation();
  const { copied, copy } = useCopy();
  return (
    <Dialog
      open={!!result?.password}
      onClose={onClose}
      dismissable={false}
      width={480}
      title={t("users.passwordTitle", { username: result?.user.username ?? "" })}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t("users.done")}
        </Button>
      }
    >
      {result && (
        <>
          <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-card)] bg-surface-2 p-3">
            <code className="flex-1 font-mono text-lg font-semibold tracking-wider break-all select-all">
              {result.password}
            </code>
            <Button size="sm" icon={copied ? "check" : "copy"} onClick={() => result.password && copy(result.password)}>
              {copied ? t("common.copied") : t("common.copy")}
            </Button>
          </div>
          <div className="flex flex-col gap-1 rounded-[var(--radius-card)] bg-blocked-bg px-3.5 py-2.5 text-sm text-blocked-fg">
            <strong>{t("users.passwordWarning")}</strong>
            <span>{t("users.passwordNext")}</span>
          </div>
        </>
      )}
    </Dialog>
  );
}

/** A bot's API tokens: list, create (secret shown once) and revoke. */
function BotTokensDialog({ bot, onClose }: { bot: UserSummary | null; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open={!!bot} onClose={onClose} width={720} title={t("users.botTokensTitle", { name: bot?.display_name ?? "" })}>
      {bot && (
        <div className="flex flex-col gap-4">
          <p className="m-0 text-sm text-text-2">
            {t("users.botTokensIntro", { username: bot.username })}{" "}
            <a href={DOCS_URL} target="_blank" rel="noreferrer">
              {t("tokens.apiDocs")}
            </a>
          </p>
          {bot.disabled && <p className="m-0 text-sm text-fail-fg">{t("users.botDisabledTokens")}</p>}
          <TokenManager botId={bot.id} />
        </div>
      )}
    </Dialog>
  );
}
